import {Component, EventEmitter, Input, Output} from '@angular/core';

import {BehaviorSubject, EMPTY} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../destroyable.component';
import {CliEmpUser} from '../../models/models.interfaces';
import {DataListAndCount} from '../api';
import {SelectEmployee} from '../employees/employees.model';
import {UserService} from '../user';
import {getInitials} from '../utils/strings';

@Component({
  selector: 'gp-employee-select',
  templateUrl: './employee-select.component.html',
  styleUrls: ['./employee-select.component.scss']
})
export class EmployeeSelectComponent extends Destroyable {
  @Input() public placeholder = 'Select';
  @Input() public customEmptyText = 'SETTINGS.search_user_to_select';
  @Input() public isMultiSelect = false;

  @Output() public selectedChange: EventEmitter<number | number[]> = new EventEmitter<number | number[]>();

  public userList: SelectEmployee[] = [];
  public selectedIds: number[] = [];
  public activeSelection: SelectEmployee[] = [];
  public isEmployeesLoading: boolean;
  public searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(private userService: UserService) {
    super();
    this.searchTerm$.asObservable()
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap((searchTerm) => {
          this.isEmployeesLoading = true;
          return this.userService.getAllEmpCliUsers(searchTerm).pipe(
            tap((data: DataListAndCount<CliEmpUser[]>) => {
              this.userList = data.items.data.map((cliEmpUser: CliEmpUser) =>
                ({
                  id: cliEmpUser?.id,
                  text: this.getSelectedUser(`${cliEmpUser?.lastName?.toUpperCase()} ${cliEmpUser?.firstName}`, cliEmpUser?.employeeNumber + '')
                }));
              this.isEmployeesLoading = false;
            }),
            catchError(() => {
              this.isEmployeesLoading = false;
              return EMPTY;
            })
          );
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public fetchUsersByKeyword(keyword: string): void {
    this.searchTerm$.next(keyword);
  }

  public selectUser(value: any): void {
    if (value.id) {
      this.selectedChange.emit(value.id);
    }
  }

  public onChecked(event: { id: any; value: boolean }): void {
    const id = +event.id;
    if (event.value) {
      if (!this.selectedIds.includes(id)) {
        this.selectedIds = [...this.selectedIds, id];
      }
    } else {
      this.selectedIds = this.selectedIds.filter((selectedId) => selectedId !== id);
    }
    this.selectedChange.emit([...this.selectedIds]);
  }

  public getSelectedUser(name: string, extraInfo: string): string {
    const parts = name.trim().split(/\s+/);
    const initials = getInitials(parts[0], parts.length > 1 ? parts[parts.length - 1] : '');
    const avatar = `<span class="gp-initials-avatar gp-avatar-md">${initials}</span>`;

    return `<div class="emp">
                <div class="emp-avatar">
                    ${avatar}
                </div>
                <div class="emp-info">
                    <div>${name}</div>
                    <small class="text-muted">${extraInfo}</small>
                </div>
            </div>`;
  }

  public reset(): void {
    this.searchTerm$.next('');
    this.selectedIds = [];
    this.userList = [];
    // a new array reference is required so the ng-select input binding is re-evaluated on every reset
    this.activeSelection = [];
    this.selectedChange.emit(this.isMultiSelect ? [] : null);
  }
}
